import React, { useCallback } from 'react'
import { IoGridOutline, IoSettingsOutline, IoRefreshOutline } from 'react-icons/io5'
import { DashboardProvider, useDashboard } from '../../context/DashboardContext'
import { DashboardGrid } from './DashboardGrid'
import { WidgetErrorBoundary } from './WidgetErrorBoundary'
import { TodaysFixturesWidget } from './widgets/TodaysFixturesWidget'
import { TopLeaguesWidget } from './widgets/TopLeaguesWidget'
import { DixonColesPicksWidget } from './widgets/DixonColesPicksWidget'
import { StrategyAlertsWidget } from './widgets/StrategyAlertsWidget'
import { CardsGoalsHighlightsWidget } from './widgets/CardsGoalsHighlightsWidget'
import { TrendingTeamsWidget } from './widgets/TrendingTeamsWidget'
import { WidgetPalette } from './WidgetPalette'
import { SearchTrigger } from '../SearchModal'
import type { PageId } from '../BottomNav'

// Widget titles for error boundary display
const WIDGET_TITLES: Record<string, string> = {
  fixtures: "Today's Fixtures",
  leagues: 'Top Leagues',
  dixon: 'D&C Top Picks',
  alerts: 'My Strategies',
  highlights: 'Cards & Goals',
  trending: 'Trending Teams',
}

interface DashboardContentProps {
  onNavigate: (page: PageId) => void
  onLeagueClick?: (apiLeagueId: number) => void
}

function DashboardContent({ onNavigate, onLeagueClick }: DashboardContentProps) {
  const { widgets, isEditing, toggleEditMode, resetToDefault, removeWidget } = useDashboard()

  const enabledWidgets = widgets.filter((w) => w.enabled)

  const renderWidget = (widgetId: string) => {
    const widgetConfig = widgets.find((w) => w.id === widgetId)
    if (!widgetConfig?.enabled) return null

    const commonProps = {
      isEditing,
      onRemove: () => removeWidget(widgetId),
      onNavigate,
      onLeagueClick,
    }

    switch (widgetConfig.type) {
      case 'fixtures':
        return <TodaysFixturesWidget {...commonProps} />
      case 'leagues':
        return <TopLeaguesWidget {...commonProps} />
      case 'dixon':
        return <DixonColesPicksWidget {...commonProps} />
      case 'alerts':
        return <StrategyAlertsWidget {...commonProps} />
      case 'highlights':
        return <CardsGoalsHighlightsWidget {...commonProps} />
      case 'trending':
        return <TrendingTeamsWidget {...commonProps} />
      default:
        return null
    }
  }

  return (
    <div className="dashboard-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-left">
          <IoGridOutline size={24} className="dashboard-header-icon" />
          <h1 className="dashboard-title">Dashboard</h1>
        </div>
        <div className="dashboard-header-center">
          <SearchTrigger />
        </div>
        <div className="dashboard-header-right">
          {isEditing && (
            <button
              className="dashboard-header-btn"
              onClick={resetToDefault}
              title="Reset to default layout"
            >
              <IoRefreshOutline size={18} />
              <span>Reset</span>
            </button>
          )}
          <button
            className={`dashboard-header-btn ${isEditing ? 'dashboard-header-btn--active' : ''}`}
            onClick={toggleEditMode}
            title={isEditing ? 'Done editing' : 'Customize dashboard'}
          >
            <IoSettingsOutline size={18} />
            <span>{isEditing ? 'Done' : 'Edit'}</span>
          </button>
        </div>
      </header>

      {/* Widget Palette (shown in edit mode) */}
      {isEditing && <WidgetPalette />}

      {/* Grid */}
      <DashboardGrid onNavigate={onNavigate}>
        {enabledWidgets.map((widget) => (
          <div key={widget.id} className="dashboard-grid-item">
            <WidgetErrorBoundary
              widgetId={widget.id}
              widgetTitle={WIDGET_TITLES[widget.type] || widget.type}
            >
              {renderWidget(widget.id)}
            </WidgetErrorBoundary>
          </div>
        ))}
      </DashboardGrid>
    </div>
  )
}

interface DashboardPageProps {
  onNavigate: (page: PageId) => void
  onLeagueClick?: (apiLeagueId: number) => void
}

export function DashboardPage({ onNavigate, onLeagueClick }: DashboardPageProps) {
  return (
    <DashboardProvider>
      <DashboardContent onNavigate={onNavigate} onLeagueClick={onLeagueClick} />
    </DashboardProvider>
  )
}
